import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";

const IMG =
  "https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbW9kZXJuJTIwc2VydmVyJTIwcm9vbXxlbnwwfHx8fDE3Nzc1MjM2MzZ8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  {
    title: "Behavior Extraction",
    desc: "Automatically capture real business logic from legacy source, runtime, and observed traffic, including COBOL, Natural/Adabas and IBM mainframe stacks.",
  },
  {
    title: "Executable Test Generation",
    desc: "Generate independent, deterministic test cases, covering happy paths, edge cases and silent regression scenarios.",
  },
  {
    title: "Parallel Run Engine",
    desc: "Run legacy and modernized components side-by-side with continuous behavioral diffing and production-safe shadow traffic.",
  },
  {
    title: "Regression Radar",
    desc: "Detect the moment any behavior drifts, with drill-downs into inputs, outputs, and responsible change sets.",
  },
  {
    title: "Audit-Grade Reports",
    desc: "Export verifiable, immutable reports suitable for internal audit, compliance, and board-level sign-offs.",
  },
  {
    title: "Zero Vendor Lock-In",
    desc: "Works alongside any modernization partner or toolchain. Keep your vendor. We'll keep them honest.",
  },
];

const USE_CASES = [
  "Modernizing IBM-era stacks (COBOL, Natural/Adabas, PL/1)",
  "Replatforming core banking, insurance, trading systems",
  "Independently verifying vendor-led rewrites",
  "Establishing behavioral baselines before modernization starts",
];

export default function ProductLegacyTesting() {
  return (
    <>
      <SEO
        title="Automated Legacy Testing Suite"
        description="A fully automated testing suite for legacy code modernization. Extract behavior, generate executable tests, run in parallel, and prove your modernized system behaves exactly like the old one."
        path="/products/legacy-testing-suite"
        image={IMG}
      />
      <section className="relative overflow-hidden border-b border-white/10 bg-obsidian">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage: `url(${IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/90 to-obsidian" />
        <div className="absolute inset-0 bg-grid opacity-50" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32">
          <div className="chip text-neon-green">&gt; product.01 // legacy_testing_suite</div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-7xl text-white tracking-tighter leading-[0.95] max-w-5xl"
          >
            Automated Legacy <span className="text-glow-green text-neon-green">Testing Suite</span>
          </motion.h1>
          <p className="mt-6 max-w-2xl text-white/70 text-lg leading-relaxed">
            A fully automated testing platform for legacy code modernization.
            Extracts behavior, generates tests, runs in parallel, and proves
            mathematically, that your new system does what the old one did.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="legacy-product-cta"
              className="group inline-flex items-center gap-2 bg-neon-green text-obsidian px-6 py-3.5 text-sm font-mono font-bold uppercase tracking-widest hover:shadow-[0_0_40px_rgba(0,255,65,0.6)] transition-all"
            >
              Request a Demo <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="mailto:contact@unitedtechlab.com"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3.5 text-sm font-mono font-semibold uppercase tracking-widest hover:border-neon-green hover:text-neon-green transition-all"
            >
              Talk to engineering
            </a>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-carbon py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="chip text-neon-green">&gt; /capabilities</div>
          <h2 className="mt-3 font-display font-black text-4xl md:text-5xl text-white leading-tight max-w-3xl">
            Built to handle systems nobody else will touch.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="bg-obsidian p-8 hover:bg-[#0f0f0f] transition-colors"
                data-testid={`legacy-feature-${i}`}
              >
                <div className="chip text-neon-green">[F.0{i + 1}]</div>
                <h3 className="mt-4 font-display font-bold text-white text-xl">{f.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-obsidian py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="chip text-neon-green">&gt; /use_cases</div>
            <h3 className="mt-3 font-display font-black text-3xl md:text-4xl text-white leading-tight">
              Where teams deploy the suite.
            </h3>
          </div>
          <ul className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {USE_CASES.map((u) => (
              <li
                key={u}
                className="flex items-start gap-3 border border-white/10 p-5 bg-carbon"
              >
                <CheckCircle2 size={18} className="text-neon-green mt-0.5 shrink-0" />
                <span className="text-sm text-white/80 leading-relaxed">{u}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CTASection
        eyebrow="> product.legacy_testing_suite"
        title="See it run on your own legacy system."
        subtitle="30-minute walkthrough. Bring a sample flow and leave with a clear picture of what's verifiable and what's at risk."
      />
    </>
  );
}
